/**
 * The session whose result is this pool (S-20, owner ruling 2026-10-14).
 *
 * The session's cut line is the truth: a pool that a session produced
 * (`prioritization_sessions.resulting_item_pool_id`) takes its line from that
 * session, and the server refuses a pool-editor move on it
 * (`set_item_pool_cut_line`, migration 890). The pool editor asks this hook so
 * it never draws a control the server will refuse (build contract §2.8), and
 * says where the line is set instead.
 *
 * Reads the session store first; when the store has not loaded that session,
 * one narrow read finds it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';

export interface SessionBehindPool {
  id: string;
  name: string;
}

export function useSessionBehindPool(poolId: string | null | undefined): SessionBehindPool | null {
  const fromStore = useSessionStore((s) =>
    poolId ? s.sessions.find((x) => x.resultingItemPoolId === poolId && !x.retiredAt) : undefined,
  );
  const [fetched, setFetched] = useState<{ poolId: string; session: SessionBehindPool | null } | null>(null);

  useEffect(() => {
    if (!poolId || fromStore) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await getTypedSupabaseClient()
          .from('prioritization_sessions')
          .select('id, name')
          .eq('resulting_item_pool_id', poolId)
          .is('retired_at', null)
          .order('created_at', { ascending: false })
          .limit(1);
        if (cancelled) return;
        const row = data?.[0];
        setFetched({ poolId, session: row ? { id: row.id, name: row.name ?? '' } : null });
      } catch {
        if (!cancelled) setFetched({ poolId, session: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolId, fromStore]);

  return useMemo(() => {
    if (!poolId) return null;
    if (fromStore) return { id: fromStore.id, name: fromStore.name ?? '' };
    return fetched?.poolId === poolId ? fetched.session : null;
  }, [poolId, fromStore, fetched]);
}
